import { authenticatedRequest } from "../api";
import { getConfig } from "../config";
import { urlParser } from "../urlParser";

import type { APIObject, ParamsType } from "./Ressource";
import { Ressource } from "./Ressource";

const url = "/organizations/:organizationId/payment-source";
const paramDefaults = {};
const creatableField = ["type", "token", "email", "chargeable"];

export type OrganizationPaymentSourceParams = {
  [key: string]: any;
  organizationId: string;
};

type PrepayFallback = "invoice_account" | "stripe_sepa_debit" | "credit-card";

export class OrganizationPaymentSource extends Ressource {
  token?: string;
  email?: string;
  type?: string;
  chargeable?: boolean;
  data?: {
    balance?: string;
    currency?: string;
    fallback?: {
      type: PrepayFallback;
    };
    mandate_reference?: string;
    mandate_url?: string;
    exp_month?: string;
    exp_year?: string;
    type?: string;
  };

  id?: string;
  name?: string;
  number?: string;
  payment_category?: string;
  type_label?: string;

  constructor(
    paymentSource: APIObject,
    customUrl?: string,
    params?: ParamsType
  ) {
    const { api_url } = getConfig();
    const { organizationId, ...paymentSourceData } = paymentSource;
    const newParams = { ...(params ?? {}), organizationId };

    super(
      customUrl ?? `${api_url}${url}`,
      paramDefaults,
      newParams,
      paymentSourceData,
      creatableField
    );

    this.token = paymentSourceData.token;
    this.email = paymentSourceData.email;
    this.type = paymentSourceData.type;
    this.chargeable = paymentSourceData.chargeable;
    this.data = paymentSourceData.data;
    this.id = paymentSourceData.id;
    this.name = paymentSourceData.name;
    this.number = paymentSourceData.number;
    this.payment_category = paymentSourceData.payment_category;
    this.type_label = paymentSourceData.type_label;
  }

  static async get(
    params: OrganizationPaymentSourceParams,
    customUrl?: string
  ) {
    const { api_url } = getConfig();
    const { organizationId, ...queryParams } = params;

    const parsedUrl = urlParser(
      customUrl ?? `${api_url}${url}`,
      { organizationId },
      paramDefaults
    );

    return authenticatedRequest(parsedUrl, "GET", queryParams)
      .then(data => {
        if (typeof data === "undefined") return;
        return new OrganizationPaymentSource(data.payment_source);
      })
      .catch(() => new OrganizationPaymentSource({}));
  }

  /**
   * Delete the payment source.
   *
   * @return object
   */
  static async delete(organizationId: string) {
    const { api_url } = getConfig();
    const parsedUrl = urlParser(
      `${api_url}${url}`,
      { organizationId },
      paramDefaults
    );
    return authenticatedRequest(parsedUrl, "DELETE");
  }

  /**
   * Get allowed payment source.
   * The list of allowed payment sources for the current API consumer.
   *
   * @return object
   */
  static async getAllowed(organizationId: string) {
    const { api_url } = getConfig();
    const parsedUrl = urlParser(
      `${api_url}${url}`,
      {
        organizationId
      },
      {}
    );
    return authenticatedRequest(`${parsedUrl}/allowed`, "GET");
  }

  /**
   * Create a Setup Intent.
   *
   * @return object
   */
  static async intent(organizationId: string) {
    const { api_url } = getConfig();
    const parsedUrl = urlParser(
      `${api_url}${url}`,
      {
        organizationId
      },
      {}
    );
    return authenticatedRequest(`${parsedUrl}/intent`, "POST");
  }

  static async setVerificationMethodAsPaymentMethod(organizationId: string) {
    const { api_url } = getConfig();
    const parsedUrl = urlParser(
      `${api_url}${url}`,
      {
        organizationId
      },
      {}
    );
    return authenticatedRequest(`${parsedUrl}`, "PATCH", { chargeable: true });
  }
}
